/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {FormattedMessage} from 'react-intl'
import {Flex, Heading} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrentBasket} from '../../../hooks/use-current-basket'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Link as RouteLink} from 'react-router-dom'
import {HOME_HREF} from '../../../constants'

/**
 * CartTitle is a functional React component that displays the title of the shopping cart
 * along with the total number of items in the cart. It also provides a link to continue
 * shopping, redirecting the user to the home page.
 *
 * This component utilizes the `useShipToCountry` and `useCurrentBasket` hooks to fetch
 * the current shipping country and the number of items in the basket, respectively.
 *
 * The title is displayed in a large, uppercase font and dynamically updates to reflect
 * the number of items in the cart, using pluralization rules for proper grammar.
 *
 * The component is styled using the `Flex` and `Heading` components for layout and typography,
 * and it uses the `FormattedMessage` component for internationalization support.
 *
 * @returns {JSX.Element} A JSX element containing the cart title and a link to continue shopping.
 */
const CartTitle = () => {
    const {
        derivedData: {totalItems, bonusProductLineItemsCount}
    } = useCurrentBasket()
    return (
        <Flex justifyContent={'space-between'} alignItems={'center'}>
            <Heading as="h1" fontSize={['3xl', null, '4xl']} textTransform="uppercase">
                <FormattedMessage
                    defaultMessage="Cart ({itemCount, plural, =0 {0 items} one {# item} other {# items}})"
                    values={{itemCount: totalItems + bonusProductLineItemsCount}}
                    id="cart_title.title.cart_num_of_items"
                />
            </Heading>
            <Link as={RouteLink} variant="link" to={HOME_HREF}>
                <FormattedMessage
                    defaultMessage="Continue Shopping"
                    id="cart_title.title.home_link"
                />
            </Link>
        </Flex>
    )
}

export default CartTitle
